import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/loanTypesSteps";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import SubTitle from "app/components/common/typography/SubTitle";
import { expenses } from "app/utils/applicant/types/types";
import LiabilitiesInputs from "app/components/Individual/applicant/liabilities/LiabilitiesInputs";

const Expenses: React.FC = () => {
  const { applicants, ...remaingData } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<expenses>({
    defaultValues: {
      totalExpenses: 0,
      monthlyRentExpenses: 0,
      privateSchooling: 0,
      privateInsurance: 0,
      childSupport: 0,
    },
  });

  const onSubmit = (data: expenses) => {
    const expenseMap: Record<keyof expenses, string> = {
      totalExpenses: "total-expense",
      monthlyRentExpenses: "monthly-rent",
      privateSchooling: "private-schooling",
      privateInsurance: "private-insurance",
      childSupport: "child-support",
    };
    const transformedExpenses = Object.entries(data).map(([key, value]) => ({
      type: expenseMap[key as keyof expenses],
      amount: Number(value) || 0,
    }));
    const existingExpenses = applicants?.expenses || [];
    const updatedExpenses = transformedExpenses.map((expense) => {
      const existing = existingExpenses.find((e) => e.type === expense.type);
      return {
        ...expense,
        ...(existing?.id && { id: existing.id }),
      };
    });
    dispatch(
      addStepsData({
        ...remaingData,
        applicants: {
          ...applicants,
          expenses: updatedExpenses,
        },
      })
    );
    dispatch(nextStep());
  };

  useEffect(() => {
    if (applicants?.expenses) {
      const getAmountByType = (type: string) =>
        applicants?.expenses?.find((expense) => expense.type === type)
          ?.amount || 0;

      reset({
        totalExpenses: getAmountByType("total-expense"),
        monthlyRentExpenses: getAmountByType("monthly-rent"),
        privateSchooling: getAmountByType("private-schooling"),
        privateInsurance: getAmountByType("private-insurance"),
        childSupport: getAmountByType("child-support"),
      });
    }
  }, [applicants, reset]);

  return (
    <>
      <ProgressBar progress={90} />
      <div className="w-full  sm:w-[70%] xl:w-[55%]  mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Expenses" />
        <SubTitle title="Please include all of your monthly expenses to the best of your knowledge to avoid rejection of your loan" />
        <form onSubmit={handleSubmit(onSubmit)}>
          <LiabilitiesInputs
            control={control}
            errors={errors}
            groups={[
              [
                {
                  label: "Total monthly living expenses",
                  name: "totalExpenses",
                  required: true,
                  tooltip:
                    "Monthly expenses for entertainment and lifestyle (including dining out, shopping, vacations, personal care, etc.)",
                },
              ],
              [
                {
                  label: "Monthly rent expenses",
                  name: "monthlyRentExpenses",
                  required: false,
                  tooltip:
                    "Your monthly rent. If you pay a mortgage, Don't include it here.",
                },
              ],
              [
                {
                  label: "Private schooling",
                  name: "privateSchooling",
                  required: false,
                  tooltip:
                    "Tuition fees and regular costs for private schools.",
                },
              ],
              [
                {
                  label: "Private insurance",
                  name: "privateInsurance",
                  required: false,
                  tooltip:
                    "Monthly payment for private health, life, or income insurance.",
                },
                {
                  label: "Child Support",
                  name: "childSupport",
                  required: false,
                  tooltip: "Legally required or agreed child support payments.",
                },
              ],
            ]}
          />

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit">Next</ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default Expenses;
